/**
 * V2ScreenAdapter - EntityAdapter implementation for V2 StepNode entities
 *
 * Adapts StepNode ↔ FlowStepNode for ReactFlow canvas consumption.
 * FlowStepNode adds entityId (= StepNode.id) for EntityRegistry compatibility.
 *
 * Key difference from OnboardingScreenAdapter (V1):
 * - Works with V2 StepNode/FlowStepNode instead of V1 Screen/FlowOnboardingScreen
 * - Uses V2TransitionManager for connection extraction (V2 edge paths)
 * - Uses clearAllStepEdges (V2 edge locations) instead of clearAllScreenEdges
 *
 * Sprint: schema-v2-migration, Sprint 2
 * Related: OnboardingScreenAdapter (V1), V2TransitionManager, V2EdgeCleaner
 */

import type { StepNode } from '@segmently/schema-models/v2';
import type { PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';
import type { Connection, CanvasNode } from './EntityAdapter';
import { validatePosition } from './validation';
import { v2TransitionManager } from '@modules/reactFlow/services/onboarding/V2TransitionManager';
import { clearAllStepEdges } from '@modules/reactFlow/utils/transformers/V2EdgeCleaner';

// Type alias for Position (matches EntityAdapter interface)
type Position = PositionOnConfigurator;

/**
 * Flow StepNode type with entityId for internal use
 * Adds entityId to V2 StepNode (which only has id)
 * Same pattern as FlowOnboardingScreen extends Screen
 */
export interface FlowStepNode extends StepNode {
  entityId: string;  // Added for react-flow compatibility (same as StepNode.id)
}

/**
 * V2ScreenAdapter - Adapter for V2 StepNode entities
 *
 * Key mapping: entityId = step.id (same value)
 * Used exclusively by V2ScreenFlowCoordinator
 */
export class V2ScreenAdapter {
  /**
   * Convert V2 StepNode to FlowStepNode
   * Adds entityId = id for react-flow compatibility
   */
  toFlowEntity(step: StepNode): FlowStepNode {
    return {
      ...step,
      entityId: step.id,
    };
  }

  /**
   * Convert FlowStepNode back to StepNode
   * Strips entityId for Firebase persistence
   */
  toFirebaseStep(flowStep: FlowStepNode): StepNode {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { entityId, ...step } = flowStep;
    return step as StepNode;
  }

  /**
   * Extract position from StepNode entity
   */
  extractPosition(step: FlowStepNode): Position {
    const position = step.position || { x: 0, y: 0 };
    validatePosition(position);
    return position;
  }

  /**
   * Update position in StepNode entity (immutable)
   */
  updatePosition(step: FlowStepNode, position: Position): FlowStepNode {
    validatePosition(position);
    return {
      ...step,
      position,
    };
  }

  /**
   * Extract connections from StepNode entity
   *
   * Delegates to V2TransitionManager (all 12 V2 edge locations)
   */
  extractConnections(step: FlowStepNode): Connection[] {
    const transitions = v2TransitionManager.extractTransitions(step);
    return transitions.map(t => ({
      targetId: t.targetEntityId,
      sourceActionKey: t.sourceActionKey,
      label: t.label,
    }));
  }

  /**
   * Convert StepNode to Canvas node
   */
  toCanvasNode(step: FlowStepNode): CanvasNode {
    return {
      id: step.id,
      name: step.name || 'Unnamed Screen',
      type: 'onboarding-screen-v2',
      position: this.extractPosition(step),
      properties: {
        entityId: step.entityId,
        entityType: 'onboarding-screen-v2',
        screenType: step.screenType,
      },
      connectionHandles: this.extractConnections(step).map((conn, idx) => ({
        id: `${step.entityId}-handle-${idx}`,
        sourceActionKey: conn.sourceActionKey,
        position: { x: 0, y: 0 },
        label: conn.label,
      })),
    };
  }

  /**
   * Create V2 StepNode from native V2 template (no V1→V2 conversion)
   *
   * For templates that are already V2 StepNodes (from themeSettingsV2.flowDocument
   * or defaultStepNodeFactory). Skips V1→V2 conversion entirely.
   *
   * Steps:
   * 1. Deep clone template
   * 2. Clear all V2 edges via clearAllStepEdges
   * 3. Generate new UUID for id/entityId
   * 4. Set position
   *
   * @param template - V2 StepNode template
   * @param position - Position for new screen on canvas
   * @returns New FlowStepNode with UUID and cleared edges
   */
  createFromV2Template(
    template: StepNode,
    position: Position,
  ): FlowStepNode {
    const uuid = crypto.randomUUID();

    // 1. Deep clone (avoid mutating original template)
    const cloned = JSON.parse(JSON.stringify(template)) as StepNode;

    // 2. Clear all V2 edges (templates shouldn't carry transitions)
    const withoutEdges = clearAllStepEdges(cloned);

    // 3. Clean stickyContainer from FlexibleLayout templates (user enables it explicitly via toggle)
    if (withoutEdges.screenType === 'FlexibleLayout') {
      const fl = (withoutEdges.content as any)?.flexibleLayout;
      if (fl?.stickyContainer) {
        console.log('[V2ScreenAdapter] Removing stickyContainer from FlexibleLayout template');
        delete fl.stickyContainer;
      }
    }

    // 4. Set new IDs and position
    return {
      ...withoutEdges,
      id: uuid,
      entityId: uuid,
      position,
    };
  }
}

// Export singleton instance
export const v2ScreenAdapter = new V2ScreenAdapter();
